/**
 * WebGL procedural planet shader and per-frame spawner sync.
 * Parallel to the spawner drawing in render/canvas2d/primitives.js drawSpawner(),
 * but uses GPU render-to-texture for the 3-D planet appearance.
 */

import { Faction, FACTION_COLORS } from '../../factions.js';
import { blendHexColors } from '../../render_utils.js';
import { Util } from '../../util.js';
import { getTintedImage } from '../../image_cache.js';
import {
    Mesh, Geometry, Shader, GlProgram, UniformGroup, Buffer, RenderTexture,
    Container, Graphics, Sprite,
} from './pixi.js';
import { texFromCanvas } from './pixi.js';
import { iconDefForEntity } from '../canvas2d/primitives.js';
import {
    PLANET_CONST,
    _glPlanetIsRocky, _planetSphereFill, _rockyPlanetSphereFill,
    PLANET_VERT, _PLANET_FRAG_GAS, PLANET_FRAG_ROCKY,
    hexToNum,
} from './shaders.js';

/**
 * Build the shared planet mesh pair (rocky + gas).
 * Sets wr._planetUniforms, wr._planetMeshRocky, wr._planetMeshGas.
 * @param {object} wr  WebGLRenderer instance
 */
export function buildPlanetShader(wr) {
    const verts  = new Float32Array([-1,-1, 1,-1, 1,1, -1,1]);
    const idx    = new Uint16Array([0,1,2, 0,2,3]);
    const posBuf = new Buffer({ data: verts, usage: 32|1024 });

    wr._planetUniforms = new UniformGroup({
        uSeed:       { value: 1.0,                         type: 'f32'       },
        uFill:       { value: PLANET_CONST.DEFAULT_FILL,   type: 'f32'       },
        uResolution: { value: new Float32Array([128, 128]), type: 'vec2<f32>' },
    });
    const geo = new Geometry({
        attributes: { aPosition: { buffer: posBuf, format: 'float32x2', stride: 8, offset: 0 } },
        indexBuffer: idx,
    });
    const mkMesh = (frag, name) => {
        const prog   = new GlProgram({ vertex: PLANET_VERT, fragment: frag, name });
        const shader = new Shader({ glProgram: prog, resources: { uniforms: wr._planetUniforms } });
        return new Mesh({ geometry: geo, shader });
    };
    wr._planetMeshRocky = mkMesh(PLANET_FRAG_ROCKY, 'planet-rocky');
    wr._planetMeshGas   = mkMesh(_PLANET_FRAG_GAS,  'planet-gas');
}

/**
 * Render a procedural planet to a new RenderTexture and return it.
 * @param {object}  wr
 * @param {number}  seed   Unique float seed for this planet's appearance.
 * @param {number}  size   Texture edge length in pixels.
 * @param {number}  fill   Sphere fill fraction (sphere edge / texture half-width).
 * @param {boolean} rocky  True → rocky shader, false → gas shader.
 * @returns {import('pixi.js').RenderTexture|null}
 */
export function renderPlanetTex(wr, seed, size, fill, rocky) {
    const mesh = rocky ? wr._planetMeshRocky : wr._planetMeshGas;
    if (!RenderTexture || !mesh) return null;
    let rt;
    try {
        rt = RenderTexture.create({ width: size, height: size, resolution: 1 });
    } catch {
        return null;
    }
    wr._planetUniforms.uniforms.uSeed = seed;
    wr._planetUniforms.uniforms.uFill = fill;
    wr._planetUniforms.uniforms.uResolution[0] = size;
    wr._planetUniforms.uniforms.uResolution[1] = size;
    wr._planetUniforms._dirtyId++;
    try {
        wr._pixi.renderer.render({ container: mesh, target: rt, clear: true });
    } catch {
        rt.destroy(true);
        return null;
    }
    return rt;
}

/**
 * Per-frame: sync the spawner sprite pool — planet discs, procedural textures,
 * tint overlays, progress bars, and bonus ship preview icons.
 * @param {object} wr
 */
export function syncSpawners(wr) {
    const spawners = wr._inner.entitySystem.spawners;
    const layer    = wr._spawnerLayer;
    const pool     = wr._spawnerPool;
    // In portrait the whole world container is rotated 90° CW; counter-rotate
    // screen-oriented elements (planet lighting, progress bar) to match
    // Canvas2D's drawSpawner behavior.
    const portrait = !!wr._inner.getWorldTransform().portrait;
    const counterRotation = portrait ? -Math.PI / 2 : 0;

    if (!wr._spawnerSizeRange && spawners.length > 0) {
        let minSize = Infinity, maxSize = -Infinity;
        for (let i = 0; i < spawners.length; i++) {
            minSize = Math.min(minSize, spawners[i].size);
            maxSize = Math.max(maxSize, spawners[i].size);
        }
        wr._spawnerSizeRange = { minSize, sizeSpan: Math.max(maxSize - minSize, 1) };
    }
    const { minSize = 24, sizeSpan = 1 } = wr._spawnerSizeRange ?? {};

    pool.forEach(e => { e._seen = false; });

    for (let i = 0; i < spawners.length; i++) {
        const s = spawners[i];
        const r = s.size / 2;

        let entry = pool.get(s);
        if (!entry) {
            const container  = new Container();
            const disc       = new Graphics();
            const planet     = new Sprite();
            const colorDisc  = new Graphics();
            planet.anchor.set(0.5);
            planet.visible = false;
            container.addChild(disc);
            container.addChild(planet);
            container.addChild(colorDisc);
            layer.addChild(container);
            const progressBar = new Graphics();
            wr._progressBarLayer.addChild(progressBar);
            const frozenSeed = 1.0 + Math.abs((s.x * 1.27 + s.y * 3.11) % 97);
            entry = { container, disc, planet, colorDisc, progressBar, bonusBg: null, bonusIcon: null, bonusOverlaySprites: [], bonusPreviewType: null, _seen: false, seed: frozenSeed };
            pool.set(s, entry);
        }
        entry._seen = true;

        entry.container.x = s.x;
        entry.container.y = s.y;

        const neutral      = '#8A8A8A';
        const captureRatio = Util.clamp01(
            s.capture.capturePointStatus / s.capture.capturePoints);
        let fillHex;
        if (s.capturedByFaction !== Faction.NEUTRAL) {
            const targetColor = FACTION_COLORS[s.capturedByFaction];
            fillHex = s.isFullyCaptured()
                ? targetColor
                : blendHexColors(neutral, targetColor, captureRatio);
        } else {
            fillHex = '#2e2e2e';
        }

        entry.disc.clear()
            .circle(0, 0, r)
            .fill({ color: hexToNum(fillHex) })
            .stroke({ color: 0x000000, alpha: 0.6, width: 3 });

        if (wr._planetMeshRocky && wr._planetMeshGas) {
            const effectiveDpr = wr._inner.game?.getEffectiveDpr?.() ?? 1;
            const seed    = entry.seed;
            const relSize = (s.size - minSize) / sizeSpan;
            const rocky   = _glPlanetIsRocky(seed, relSize);
            const fill    = rocky ? _rockyPlanetSphereFill(seed) : _planetSphereFill(seed);
            const displaySize = s.size * wr._scaleFactor * effectiveDpr;
            const texSize = Math.min(512, Math.max(64, Math.ceil(displaySize / fill / 32) * 32));

            let cached = wr._planetTexCache.get(s);
            if (!cached) {
                const rt = renderPlanetTex(wr, seed, texSize, fill, rocky);
                if (rt) { cached = { rt, size: texSize, fill, rocky }; wr._planetTexCache.set(s, cached); }
            } else if (texSize > cached.size * 1.5 || Math.abs(fill - cached.fill) > 0.03 || rocky !== cached.rocky) {
                cached.rt.destroy(true);
                const rt = renderPlanetTex(wr, seed, texSize, fill, rocky);
                cached = rt ? { rt, size: texSize, fill, rocky } : null;
                if (cached) wr._planetTexCache.set(s, cached);
                else        wr._planetTexCache.delete(s);
            }

            if (cached?.rt) {
                if (entry.planet.texture !== cached.rt) entry.planet.texture = cached.rt;
                const drawSize = s.size / cached.fill;
                entry.planet.width    = drawSize;
                entry.planet.height   = drawSize;
                entry.planet.rotation = counterRotation;
                entry.planet.visible  = true;
            } else {
                entry.planet.visible = false;
            }
        } else {
            entry.planet.visible = false;
        }

        const overlayR = s.size * 0.5;
        entry.colorDisc.clear();
        if (s.capturedByFaction !== Faction.NEUTRAL) {
            const fColor = hexToNum(FACTION_COLORS[s.capturedByFaction]);
            const alpha  = s.isFullyCaptured() ? 0.38 : captureRatio * 0.38;
            entry.colorDisc.circle(0, 0, overlayR).fill({ color: fColor, alpha });
        }

        entry.progressBar.clear();
        entry.progressBar.position.set(s.x, s.y);
        entry.progressBar.rotation = counterRotation;
        const showBar = (s.capturedByFaction !== Faction.NEUTRAL && captureRatio < 1.0) || s.labelText;
        if (showBar) {
            const barW = r * 2.4;
            const barH = 5;
            const pad  = 2;
            const bgW  = barW + pad * 2;
            const bgH  = barH + pad * 2;
            const bgX  = -bgW / 2;
            const bgY  = r + 4;

            entry.progressBar.roundRect(bgX, bgY, bgW, bgH, 2).fill({ color: 0x000000, alpha: 0.65 });

            const barX = bgX + pad;
            const barY = bgY + pad;
            if (s.labelText) {
                entry.progressBar.roundRect(barX, barY, barW, barH, 1).fill({ color: 0xFFFFFF, alpha: 0.55 });
            } else {
                const barFill = barW * captureRatio;
                const fColor  = hexToNum(FACTION_COLORS[s.capturedByFaction]);
                if (barFill > 0) entry.progressBar.roundRect(barX, barY, barFill, barH, 1).fill({ color: fColor, alpha: 0.95 });
            }
        }

        const preview = s.bonusShipPreview;
        if (preview) {
            const previewType = preview.type;
            if (entry.bonusPreviewType !== previewType) {
                if (entry.bonusBg)   { entry.container.removeChild(entry.bonusBg);   entry.bonusBg.destroy(); entry.bonusBg = null; }
                if (entry.bonusIcon) { entry.container.removeChild(entry.bonusIcon); entry.bonusIcon.destroy({ texture: false }); entry.bonusIcon = null; }
                for (const s2 of entry.bonusOverlaySprites) { entry.container.removeChild(s2); s2.destroy({ texture: false }); }
                entry.bonusOverlaySprites = [];

                const bgRadius = (r * 0.85) * 0.6;
                const bg = new Graphics();
                bg.circle(0, 0, bgRadius).fill({ color: 0x000000, alpha: 0.65 });
                entry.container.addChild(bg);
                entry.bonusBg = bg;

                const iconDef = iconDefForEntity(preview);
                if (iconDef) {
                    const tinted = getTintedImage(iconDef.path, '#FFFFFF');
                    if (tinted) {
                        const icon = new Sprite();
                        icon.anchor.set(0.5);
                        const iSize = preview.size * 2 * iconDef.scale;
                        icon.width  = iSize;
                        icon.height = iSize;
                        icon.texture = texFromCanvas(tinted);
                        entry.container.addChild(icon);
                        entry.bonusIcon = icon;

                        const OVERLAY_SCALE = 2 / 3;
                        const overlays = preview.visual?.overlays ?? [];
                        let allOverlaysReady = true;
                        for (const ov of overlays) {
                            if (ov.type !== 'svg') continue;
                            const ovColor  = ov.opts.color ?? '#FFFFFF';
                            const ovCanvas = getTintedImage(ov.src, ovColor);
                            if (!ovCanvas) { allOverlaysReady = false; continue; }
                            const ovSprite = new Sprite();
                            ovSprite.anchor.set(0.5);
                            const ovSize = (preview.size * 2 * (ov.opts.sizeMult ?? 1.0)) * OVERLAY_SCALE;
                            ovSprite.width   = ovSize;
                            ovSprite.height  = ovSize;
                            ovSprite.alpha   = ov.opts.alpha ?? 1.0;
                            ovSprite.texture = texFromCanvas(ovCanvas);
                            entry.container.addChild(ovSprite);
                            entry.bonusOverlaySprites.push(ovSprite);
                        }

                        if (allOverlaysReady) entry.bonusPreviewType = previewType;
                    }
                } else {
                    entry.bonusPreviewType = previewType;
                }
            }
        } else if (entry.bonusBg || entry.bonusIcon || entry.bonusOverlaySprites.length) {
            if (entry.bonusBg)   { entry.container.removeChild(entry.bonusBg);   entry.bonusBg.destroy(); entry.bonusBg = null; }
            if (entry.bonusIcon) { entry.container.removeChild(entry.bonusIcon); entry.bonusIcon.destroy({ texture: false }); entry.bonusIcon = null; }
            for (const s2 of entry.bonusOverlaySprites) { entry.container.removeChild(s2); s2.destroy({ texture: false }); }
            entry.bonusOverlaySprites = [];
            entry.bonusPreviewType = null;
        }
    }

    pool.forEach((entry, spawner) => {
        if (!entry._seen) {
            layer.removeChild(entry.container);
            entry.container.destroy({ children: true });
            if (entry.progressBar && wr._progressBarLayer) {
                wr._progressBarLayer.removeChild(entry.progressBar);
                entry.progressBar.destroy();
            }
            pool.delete(spawner);
        }
    });
}
